/**
 * StyleSlotRow — a single row in the properties panel showing one style slot.
 * Single responsibility: display the current style state of one slot and open/clear it.
 */

import React, { useRef } from 'react';
import type { StyleProperties } from './types';

export interface StyleSlotRowProps {
  slotKey: string;
  slotLabel: string;
  value: StyleProperties | undefined;
  inheritedValue?: StyleProperties;
  onOpen: (slot: string, anchorEl: HTMLElement) => void;
  onClear: (slot: string) => void;
  isDark: boolean;
}

/**
 * Returns up to 3 non-empty, non-false key=value pairs from a StyleProperties object.
 */
function getPreviewEntries(style: StyleProperties): [string, string][] {
  return (Object.entries(style) as [keyof StyleProperties, unknown][])
    .filter(([, v]) => v !== undefined && v !== null && v !== false && v !== '' && v !== 0)
    .slice(0, 3)
    .map(([k, v]) => [String(k), String(v)]);
}

/**
 * StyleSlotRow — slot label + preview chip + style/clear buttons.
 */
export const StyleSlotRow: React.FC<StyleSlotRowProps> = ({
  slotKey,
  slotLabel,
  value,
  inheritedValue,
  onOpen,
  onClear,
  isDark,
}) => {
  const btnRef = useRef<HTMLButtonElement>(null);

  const accent = isDark ? '#22c55e' : '#16a34a';
  const textPrimary = isDark ? '#e2e8f0' : '#1e293b';
  const textSecondary = isDark ? '#94a3b8' : '#64748b';
  const borderColor = isDark ? 'rgba(51,65,85,0.5)' : '#e2e8f0';
  const chipBg = isDark ? 'rgba(51,65,85,0.4)' : '#f1f5f9';
  const rowBg = 'transparent';

  const hasValue = value !== undefined && Object.keys(value).length > 0;
  const hasInherited = !hasValue && inheritedValue !== undefined && Object.keys(inheritedValue).length > 0;

  const previewEntries = hasValue ? getPreviewEntries(value!) : [];

  const rowStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    height: 32,
    padding: '0 2px',
    background: rowBg,
  };

  const labelStyle: React.CSSProperties = {
    fontSize: 11,
    fontWeight: 600,
    color: textPrimary,
    minWidth: 64,
    flexShrink: 0,
  };

  const previewChipStyle: React.CSSProperties = {
    flex: 1,
    display: 'flex',
    alignItems: 'center',
    gap: 3,
    overflow: 'hidden',
    minWidth: 0,
  };

  const chipStyle: React.CSSProperties = {
    fontSize: 10,
    fontFamily: 'monospace',
    color: textSecondary,
    background: chipBg,
    border: `1px solid ${borderColor}`,
    borderRadius: 3,
    padding: '1px 5px',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    maxWidth: 80,
  };

  const inheritedChipStyle: React.CSSProperties = {
    fontSize: 10,
    fontFamily: 'monospace',
    color: accent,
    opacity: 0.7,
    background: `${accent}12`,
    border: `1px solid ${accent}30`,
    borderRadius: 3,
    padding: '1px 5px',
    whiteSpace: 'nowrap',
  };

  const iconBtnStyle: React.CSSProperties = {
    background: 'none',
    border: `1px solid ${borderColor}`,
    borderRadius: 4,
    cursor: 'pointer',
    color: textSecondary,
    fontSize: 12,
    padding: '2px 7px',
    lineHeight: 1.4,
    flexShrink: 0,
    display: 'flex',
    alignItems: 'center',
  };

  const clearBtnStyle: React.CSSProperties = {
    ...iconBtnStyle,
    color: isDark ? '#ef4444' : '#dc2626',
    borderColor: isDark ? 'rgba(239,68,68,0.3)' : 'rgba(220,38,38,0.3)',
  };

  const handleOpenClick = () => {
    if (btnRef.current) {
      onOpen(slotKey, btnRef.current);
    }
  };

  return (
    <div style={rowStyle}>
      <span style={labelStyle}>{slotLabel}</span>

      {/* Preview chip area */}
      <div style={previewChipStyle}>
        {hasInherited && (
          <span style={inheritedChipStyle} title="Inherited from parent">
            ↑ inherited
          </span>
        )}
        {previewEntries.map(([k, v]) => (
          <span key={k} style={chipStyle} title={`${k}: ${v}`}>
            {k}:{v}
          </span>
        ))}
        {!hasValue && !hasInherited && (
          <span style={{ fontSize: 10, color: textSecondary, opacity: 0.5 }}>—</span>
        )}
      </div>

      {/* Open style editor */}
      <button
        ref={btnRef}
        type="button"
        style={iconBtnStyle}
        onClick={handleOpenClick}
        aria-label={`Open style editor for ${slotLabel}`}
        title={`Style ${slotLabel}`}
      >
        ⬡ Style
      </button>

      {/* Clear button — only shown if value is set */}
      {hasValue && (
        <button
          type="button"
          style={clearBtnStyle}
          onClick={() => onClear(slotKey)}
          aria-label={`Clear ${slotLabel} styles`}
          title="Clear styles"
        >
          ×
        </button>
      )}
    </div>
  );
};

StyleSlotRow.displayName = 'StyleSlotRow';
